import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    linkedSignal,
    signal,
    untracked,
} from '@angular/core';
import { applyEach, applyWhenValue, FieldTree, form, readonly, required, submit } from '@angular/forms/signals';
import {
    addressRequired,
    AppNotificationService,
    AppNotificationType,
    BoundConflict,
    boundsCollection,
    chronologicallyOrdered,
    cityWithUsState,
    CONCURRENT_USAGE_ERROR_CODES,
    ConfigConstants,
    CONFIRMATION_TYPES,
    CONNECTION_ERROR_CODES,
    coversSpan,
    DateUtilities,
    exceedsMaxWorkingHours,
    followUpTravelType,
    getUuidV4,
    isEndOfWorkInFuture,
    LoadingScreenComponent,
    LoadingScreenModel,
    ModalClass,
    notEarlierThan,
    NotificationUtilities,
    notTooFarInFuture,
    selectAddressesById,
    selectDisplayAdditionalTravelBack,
    selectDisplayMeansOfTransport,
    selectDisplayTimesheetAddress,
    selectEnableStandbyTravel,
    selectEowFlexibleHours,
    selectHasEntriesInProgressOrToBeSigned,
    selectLatestSubmittedEndOfWork,
    selectMaxWorkingHoursInTimesheet,
    selectMeansOfTransportOptions,
    selectSelectedTimesheet,
    selectSelectedTimesheetFormModel,
    selectSelectedTimesheetReadonly,
    selectSelectedTimesheetRows,
    selectTimesheetPauseCodeList,
    selectTimesheetStatus,
    selectTimesheetSummaries,
    selectTimesheetTaskTypeOptions,
    selectTimesheetWorkCodeDescriptions,
    SpanConflict,
    SpanCoverage,
    submitTimesheetRequested,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimesheetAddressState,
    timesheetEdited,
    timesheetEntryDeleted,
    TimesheetFormEntry,
    TimesheetFormModel,
    TimesheetGapRow,
    timesheetManualEntryAdded,
    TimesheetManualRow,
    timesheetOrderConflicts,
    timesheetSelected,
    TimesheetState,
    timesheetSubmitFailed,
    timesheetSubmitSynced,
    TimesheetWorkRow,
    TimeSpanFields,
    toEntryPatches,
    toTimesheetAddress,
    withEndOfWorkFollowingTravelBack,
    withStartOfWorkPrecedingTravelTo,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { firstValueFrom, Observable, of } from 'rxjs';
import { TimesheetCanDeactivateHost } from '../../timesheet-can-deactivate.interface';
import { AddressPickerComponent } from './address-picker/address-picker.component';
import { TimesheetGapRowComponent } from './rows/timesheet-gap-row.component';
import { TimesheetManualRowComponent } from './rows/timesheet-manual-row.component';
import { TimesheetMarkerRowComponent } from './rows/timesheet-marker-row.component';
import { TimesheetWorkRowComponent } from './rows/timesheet-work-row.component';
import { TimesheetPickerComponent } from './timesheet-picker/timesheet-picker.component';

/**
 * The working day as the coverage rule reads it: bracketed by the markers, filled by the entries.
 *
 * Values rather than fields, because an open gap is a property of the day as a whole and belongs to
 * no field — the gap row already shows the user where it is.
 *
 * @param model the form model being validated
 * @returns the outer span and the entries that must account for it
 */
const workingDayCoverage = (model: TimesheetFormModel): SpanCoverage => ({
    start: model.startOfWork,
    end: model.endOfWork,
    items: model.entries,
});

/**
 * The two instant fields of each entry, in model order.
 *
 * Fields rather than values, because the ordering rules mark the row at fault: which is what puts
 * the message on the row being edited rather than only at the foot of the page.
 *
 * @param entries the entry collection's field
 * @returns the start and end field of every entry
 */
const entrySpanFields = (entries: FieldTree<TimesheetFormEntry[]>): readonly TimeSpanFields[] =>
    Array.from(entries, (entry) => ({ start: entry.start, end: entry.end }));

/**
 * Store-connected replacement for `TimesheetAndExpensesComponent`, routed behind the
 * `environment.enableNgRx` flag.
 *
 * Everything it renders comes from selectors and everything it changes goes out as an action —
 * no facade, and no `ConfigService` / `ValueHelperService` / `UserMasterService` either; their
 * slices are read directly. There is no tab wrapper: expenses are gone from the timesheet, so
 * the timesheet is the whole page.
 *
 * The page owns the store connection: it binds one form to the store's own projection of the
 * timesheet, validates it through one schema, and writes each edit straight back as an action —
 * bar one that would put the timesheet's times at odds with each other, which is kept in the form
 * and marked instead. The rows below it are dumb and bind to that model, and the address picker is
 * the only child that reads a slice of its own.
 */
@Component({
    selector: 'pace-timesheet-ngrx',
    standalone: true,
    imports: [
        TranslateModule,
        TimesheetPickerComponent,
        TimesheetGapRowComponent,
        TimesheetManualRowComponent,
        TimesheetMarkerRowComponent,
        TimesheetWorkRowComponent,
    ],
    templateUrl: './timesheet-ngrx.component.html',
    styleUrl: './timesheet-ngrx.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimesheetNgrxComponent implements TimesheetCanDeactivateHost {
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);
    private readonly modalService = inject(BsModalService);
    private readonly actions$ = inject(Actions);
    private readonly router = inject(Router);
    private readonly appNotificationService = inject(AppNotificationService);
    private readonly notificationUtilities = inject(NotificationUtilities);

    /** Monotonic counter correlating a write with the outcome the persist effect reports. */
    private writeCounter = 0;

    /** The selectable timesheets, newest first. */
    protected readonly summaries = this.store.selectSignal(selectTimesheetSummaries);

    /**
     * The timesheet on show. Read through the resolving selector rather than from
     * `selectedTimesheetId`, so the page and the picker agree even before anything is picked and
     * after a rehydrate has replaced the timesheets.
     */
    protected readonly selectedTimesheet = this.store.selectSignal(selectSelectedTimesheet);

    /** The rows to render: the markers, the entries, and the gaps between them. */
    protected readonly rows = this.store.selectSignal(selectSelectedTimesheetRows);

    /** A submitted timesheet is shown read-only. */
    protected readonly isReadonly = this.store.selectSignal(selectSelectedTimesheetReadonly);

    /** A timesheet with a confirmation still in progress may be viewed but not submitted. */
    protected readonly hasInProgressOrToBeSignedConfirmations = this.store.selectSignal(
        selectHasEntriesInProgressOrToBeSigned,
    );

    /** Whether the slice has finished its first rehydrate. */
    protected readonly status = this.store.selectSignal(selectTimesheetStatus);

    protected readonly displayAddress = this.store.selectSignal(selectDisplayTimesheetAddress);
    private readonly displayMeansOfTransport = this.store.selectSignal(selectDisplayMeansOfTransport);
    private readonly displayAdditionalTravelBack = this.store.selectSignal(selectDisplayAdditionalTravelBack);
    private readonly enableStandbyTravel = this.store.selectSignal(selectEnableStandbyTravel);
    private readonly maxWorkingHours = this.store.selectSignal(selectMaxWorkingHoursInTimesheet);
    private readonly pauseCodes = this.store.selectSignal(selectTimesheetPauseCodeList);

    /** The address slice, keyed by id, as read straight from the store. */
    private readonly addressesById = this.store.selectSignal(selectAddressesById);

    /**
     * The same lookup, projected to the timesheet-shaped address `addressRequired` checks.
     *
     * A `computed` rather than a second selector: the projection is a one-line map over what
     * `addressesById` already read, so a selector would only add a second memoized read of the
     * same slice for no benefit.
     */
    private readonly timesheetAddressesById = computed(
        (): ReadonlyMap<string, TimesheetAddressState> =>
            new Map([...this.addressesById()].map(([id, address]) => [id, toTimesheetAddress(address)])),
    );

    /** How many hours past now `endOfWork` may still be typed as, before the schema rejects it. */
    private readonly eowFlexibleHours = this.store.selectSignal(selectEowFlexibleHours);

    /**
     * The latest instant an already submitted timesheet accounted for. The working day may not begin
     * before it — two timesheets claiming the same time would each be booked in full.
     */
    private readonly latestSubmittedEndOfWork = this.store.selectSignal(selectLatestSubmittedEndOfWork);

    /** Guards against a second send while one is in flight. */
    protected readonly isSubmitting = signal(false);

    /**
     * The end-of-work value whose future-dated warning has been acknowledged.
     *
     * Stored as the value rather than a flag so changing the end of work re-arms the warning: a
     * different future instant is a new thing to be told about.
     */
    private readonly acknowledgedFutureEndOfWork = signal<string | undefined>(undefined);

    /** Whether the long-working-day warning has been acknowledged for this visit. */
    private readonly acknowledgedLongDay = signal(false);

    /**
     * The current instant, read at the moment a check needs it.
     *
     * A method rather than a signal on purpose: the submit gates compare against "now at the time
     * the technician pressed send", and a memoized value would compare against whenever the page
     * last recomputed.
     *
     * @returns the current instant as an ISO timestamp
     */
    private now(): string {
        return new Date().toISOString();
    }

    /** Means of transport on offer, for the rows that let one be chosen. */
    protected readonly meansOfTransportOptions = this.store.selectSignal(selectMeansOfTransportOptions);

    private readonly meansOfTransportDescriptions = computed(() =>
        Object.fromEntries(this.meansOfTransportOptions().map((option) => [option.value, option.label])),
    );

    /**
     * The two kinds a travel-back leg can take. Built here rather than read from value help: they
     * are a fixed pair of work codes, which is what the legacy `travelBackDropdown` also hard-coded.
     */
    protected readonly travelTypeOptions = computed(() => [
        {
            value: TIMERECORDING_TYPE_OF_WORK_CODES.TRBK,
            label: this.message('TIMESHEET_TRAVEL_BACK'),
        },
        {
            value: TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY,
            label: this.message('TIMESHEET_TRAVEL_BACK_STANDBY'),
        },
    ]);

    /** The selected timesheet as the form edits it, straight from the store. */
    private readonly storedModel = this.store.selectSignal(selectSelectedTimesheetFormModel);

    /**
     * What the form binds to.
     *
     * A `linkedSignal` rather than a plain one because a form model has to be writable and this
     * one is not the truth: the store is. It follows the store, the form writes into it, and
     * {@link writeThrough} sends every such write straight back out as an action — so there is no
     * draft to keep in step and no commit to remember. The rows below are drawn from the same
     * selector, which is what makes a gap appear the moment the times that opened it are typed.
     *
     * The round trip settles because the values compare equal once the store has taken the edit:
     * the store pushes its own version back, the model matches it, and nothing further is sent.
     * A normalizing reducer — the entry sort, most visibly — simply wins, since what it produced
     * is what comes back.
     */
    protected readonly model = linkedSignal<TimesheetFormModel, TimesheetFormModel>({
        source: this.storedModel,
        computation: (fromStore) => fromStore,
    });

    /**
     * Sends anything the form put into the model back to the store, unless it would put the
     * timesheet's own times at odds with one another.
     *
     * The one place a form edit becomes an action. It compares against the store rather than
     * tracking dirtiness so that an edit the reducer refused or reshaped needs no bookkeeping
     * here: whatever the store ends up holding is what the model is reconciled to.
     *
     * A refused edit is *kept in the form* rather than undone — the model only follows the store
     * when the store emits, so the typed value stays on screen, the schema marks it, and the store
     * goes on holding the times it had. That is what stops an out-of-order entry from being sorted
     * into a new position under the user's hands: nothing may move the rows about except an edit
     * that makes sense, and while the page is open nothing but these writes moves them at all.
     *
     * It also lets an edit be made in more than one step. Dragging a slot later means typing a
     * start that is briefly past its own end; that intermediate state is refused, stays in the
     * form, and goes out as soon as the end follows it.
     *
     * Two normalizations happen before any of that: {@link withEndOfWorkFollowingTravelBack} moves
     * the end of work out to match an extended travel-back leg, and {@link
     * withStartOfWorkPrecedingTravelTo} moves the start of work back to match a travel-to leg
     * pulled earlier — so the containment check below sees a day that already accounts for the
     * longer leg rather than one the extension would overrun. They have to run first — the
     * conflict check that follows compares against whatever `edited` already holds, so a widened
     * marker has to be in it before that comparison is made.
     */
    private readonly writeThrough = effect(() => {
        const edited = withStartOfWorkPrecedingTravelTo(withEndOfWorkFollowingTravelBack(this.model()));
        const stored = untracked(this.storedModel);
        if (edited.timesheetId && !isEqual(edited, stored) && !this.wouldWorsenOrder(edited, stored)) {
            this.store.dispatch(this.editAction(edited));
        }
    });

    /**
     * Whether an edit disagrees with the rest of the timesheet more than the stored one already did.
     *
     * Measured against the stored state rather than against nothing, so a timesheet that was already
     * inconsistent when it loaded can still be repaired — refusing every write while any conflict
     * stood would lock exactly the timesheet that most needs an edit. Two confirmations recording
     * overlapping times is the case that would otherwise strand a technician.
     *
     * @param edited the model as the form now holds it
     * @param stored the model as the store holds it
     * @returns whether the edit introduces a disagreement the store did not already have
     */
    private wouldWorsenOrder(edited: TimesheetFormModel, stored: TimesheetFormModel): boolean {
        return timesheetOrderConflicts(edited).length > timesheetOrderConflicts(stored).length;
    }

    /**
     * Validation lives here, with the form — not in the field components, which stay reusable, and
     * not in the reducer, which only applies what it is given.
     */
    protected readonly timesheetForm = form(this.model, (path) => {
        // One rule covers the whole page rather than a `readonly` input threaded through every row.
        readonly(path, () => this.isReadonly());

        // One rule per instant: an instant is one field, and a half-typed one reports itself unset.
        required(path.startOfWork, { message: this.message('TIMESHEET_START_OF_WORK_LABEL') });
        required(path.endOfWork, { message: this.message('TIMESHEET_END_OF_WORK_LABEL') });

        // No entry may run backwards or reach into its neighbour.
        chronologicallyOrdered(path.entries, entrySpanFields, {
            message: (conflict) => this.conflictMessage(conflict),
        });

        // The working day must be accounted for end to end; an open gap is what this catches.
        coversSpan(path, workingDayCoverage, { message: this.message('TIMESHEET_INVALID_MESSAGE') });

        // startOfWork must be the earliest thing on the timesheet and endOfWork the latest —
        // checked one marker at a time so each carries a message that names exactly what it is
        // required to be, rather than a message generic enough to cover both. Both the marker and
        // whichever entry contradicts it are marked, each naming the instant it may not cross.
        boundsCollection(
            path,
            (tree) => ({
                bound: tree.startOfWork,
                items: entrySpanFields(tree.entries).map((entry) => entry.start),
                edge: 'earliest',
            }),
            {
                message: (conflict) =>
                    this.boundConflictMessage(
                        conflict,
                        'TIMESHEET_ERROR_START_OF_WORK_MUST_BE_FIRST',
                        'TIMESHEET_ERROR_BEFORE_START_OF_WORK',
                    ),
            },
        );
        boundsCollection(
            path,
            (tree) => ({
                bound: tree.endOfWork,
                items: entrySpanFields(tree.entries).map((entry) => entry.end),
                edge: 'latest',
            }),
            {
                message: (conflict) =>
                    this.boundConflictMessage(
                        conflict,
                        'TIMESHEET_ERROR_END_OF_WORK_MUST_BE_LAST',
                        'TIMESHEET_ERROR_AFTER_END_OF_WORK',
                    ),
            },
        );

        // The day may not reach back into a day already sent: a submitted timesheet is settled, and
        // starting a new one before it ended would book the same time twice.
        notEarlierThan(path.startOfWork, this.latestSubmittedEndOfWork, {
            message: (limit) => this.formatMessage('TIMESHEET_ERROR_START_OF_WORK_BEFORE_SUBMITTED_END_OF_WORK', limit),
        });

        // The end of work may not be typed arbitrarily far ahead of now — configurable slack for
        // clock drift and finishing up the report, not an invitation to pre-date the whole day.
        notTooFarInFuture(path.endOfWork, this.eowFlexibleHours, {
            message: (limit) => this.formatMessage('TIMESHEET_ERROR_END_OF_WORK_TOO_FAR_IN_FUTURE', limit),
        });

        // On a day with no entries yet there is nothing for the two markers to bracket, so
        // boundsCollection above has nothing to check them against — the two markers are compared
        // directly against each other instead. The one case coversSpan cannot catch either, since
        // it only ever measures forward from startOfWork.
        chronologicallyOrdered(
            path,
            (tree) =>
                entrySpanFields(tree.entries).length === 0 ? [{ start: tree.startOfWork, end: tree.endOfWork }] : [],
            {
                message: (conflict) =>
                    this.formatMessage('TIMESHEET_ERROR_END_OF_WORK_MUST_BE_AFTER_START_OF_WORK', conflict.limit),
            },
        );

        applyEach(path.entries, (entry) => {
            required(entry.start, { message: this.message('TIMESHEET_TABLE_START_LABEL') });
            required(entry.end, { message: this.message('TIMESHEET_TABLE_END_LABEL') });

            // While the owning confirmation is still open the whole entry belongs to it — the
            // travel-back codes below included.
            readonly(entry, (context) => context.value().inProgress);

            // The times a confirmation recorded stay the confirmation's, with two exceptions: the
            // start of the first settled travel-to leg and the end of the last settled travel-back
            // leg. A correction to anything else is made in the confirmation component, which the
            // row's notification number links to. The travel-back codes also stay the timesheet's
            // own, because which leg is billable or standby and by which transport is not what the
            // confirmation says.
            applyWhenValue(
                entry,
                (value) => value.kind === 'work',
                (workEntry) => {
                    // The start of the travel-to leg is the exception: a confirmation is written
                    // after the journey to the customer has begun, so its recorded start is a guess
                    // and the timesheet is where it is corrected.
                    readonly(workEntry.start, (context) => !context.valueOf(workEntry.isTravelToEditable));
                    // The end of the travel-back leg is the exception: a confirmation is written
                    // before the journey home is over, so its recorded end is a guess and the
                    // timesheet is where it is corrected.
                    readonly(workEntry.end, (context) => !context.valueOf(workEntry.isTravelBackEditable));
                },
            );

            // The address and the task type belong to manual entries only: a work entry brings
            // the confirmation's address and its work code is not editable. Applying these
            // per-kind rather than to every entry is what keeps a work entry — whose task type is
            // always empty — from failing the `required` below.
            applyWhenValue(
                entry,
                (value) => value.kind === 'manual',
                (manualEntry) => {
                    addressRequired(manualEntry.address, this.displayAddress, this.timesheetAddressesById, {
                        message: this.message('TIMESHEET_ADDRESS_LABEL'),
                    });
                    // travelType is only filled for Standby Travel entries and is filled automatically on
                    // adding this entry. So taskType is only required for manual entries where travelType is not there.
                    required(manualEntry.taskType, {
                        when: (context) => !context.valueOf(manualEntry.travelType),
                        message: this.message('TIMESHEET_TABLE_ACTIVITY_LABEL'),
                    });
                },
            );
        });
    });

    /** Whether the form currently holds a valid, submittable timesheet. */
    protected readonly canSubmit = computed(
        () => !this.isReadonly() && !this.hasInProgressOrToBeSignedConfirmations() && this.timesheetForm().valid(),
    );

    /** Task types a manual entry may be given, from the value-help slice. */
    protected readonly taskTypeOptions = this.store.selectSignal(selectTimesheetTaskTypeOptions);

    private readonly workCodeDescriptions = this.store.selectSignal(selectTimesheetWorkCodeDescriptions);

    /**
     * The unaccounted span sitting immediately before the end of work, when there is one.
     *
     * Its `start` is the instant the day is accounted for up to — the last entry's end, or the start
     * of work on a timesheet that holds no entries yet. Both the button's visibility and what
     * collapsing it writes are read from this one row, so the two cannot disagree about whether
     * there is a gap to close.
     */
    private readonly trailingGap = computed(() => {
        const rows = this.rows();
        const beforeEndOfWork = rows.length > 1 ? rows.at(-2) : undefined;
        return beforeEndOfWork?.kind === 'gap' ? beforeEndOfWork : undefined;
    });

    /** Whether a gap sits before the end of work that the user can collapse. */
    protected readonly hasTrailingGap = computed(() => this.trailingGap() !== undefined);

    /**
     * @param timesheetId the timesheet the user picked
     */
    protected onTimesheetSelected(timesheetId: string): void {
        this.store.dispatch(timesheetSelected({ timesheetId }));
    }

    /**
     * The form field for one entry, matched by id rather than by index: the rows include gaps and
     * markers, so a row's position is not its position in the model.
     *
     * @param entryId the entry to look up
     * @returns its field, or undefined when the entry has no row
     */
    protected entryField(entryId: string): FieldTree<TimesheetFormEntry> | undefined {
        const index = this.model().entries.findIndex((entry) => entry.entryId === entryId);
        return index < 0 ? undefined : this.timesheetForm.entries[index];
    }

    /**
     * @param workCode the code to describe
     * @returns the code's description, or the code itself when the value help has none
     */
    protected workCodeDescription(workCode: string): string {
        return this.workCodeDescriptions()[workCode] ?? workCode;
    }

    /**
     * Read off the row rather than looked up in the notification slice: a notification leaves the
     * device once its confirmation has been submitted, and the row still has to name what it was
     * booked against for as long as the timesheet is kept.
     *
     * The short-confirmation label is a second line the row adds on its own from `row().confirmationType`
     * — kept out of this string so the row's template owns its own layout rather than parsing line
     * breaks out of translated content.
     *
     * @param row the work row to describe
     * @returns the notification's description, consisting of type, qmnum, manum
     */
    protected notificationDescription(row: TimesheetWorkRow): string {
        return this.notificationUtilities.getNotificationTypeDescriptionFor(
            row.notificationType,
            row.notificationId.qmNum,
            row.notificationId.maNum,
        );
    }

    /**
     * What a manual row's address button shows: the chosen place, or '' while none resolves.
     *
     * Resolved here rather than in the row, which stays dumb and binds to what it is given — the
     * row only knows the entry's address id, not the address slice.
     *
     * @param row the manual row whose address is being labelled
     * @returns the address's city or free text, or '' when the id is unset or unknown
     */
    protected manualRowAddressLabel(row: TimesheetManualRow): string {
        const address = row.address ? this.timesheetAddressesById().get(row.address) : undefined;
        return cityWithUsState(address) ?? address?.addrText ?? '';
    }

    /**
     * The city on a work row's second line, resolved the same way as {@link manualRowAddressLabel}.
     *
     * @param row the work row whose address is being resolved
     * @returns the address's city, or '' when the id is unset or unknown
     */
    protected workRowAddressCity(row: TimesheetWorkRow): string {
        const address = row.address ? this.timesheetAddressesById().get(row.address) : undefined;
        return address?.city ?? '';
    }

    /**
     * Closes a gap with a manual entry spanning exactly it.
     *
     * Structural rather than a field edit, so it carries its own action: the model follows the
     * store, which is what makes the new row appear with its fields already bound.
     *
     * @param gap the gap being accounted for
     */
    protected onAddManualEntry(gap: TimesheetGapRow): void {
        const timesheet = this.selectedTimesheet();
        if (!timesheet) {
            return;
        }
        this.store.dispatch(
            timesheetManualEntryAdded({
                timesheetId: timesheet.timesheetId,
                entryId: getUuidV4(),
                start: gap.start,
                end: gap.end,
                // A manual entry closing a plain gap belongs to no notification.
                notificationId: { frontId: '', qmNum: '', maNum: '' },
                correlationId: `timesheet-add-entry-${++this.writeCounter}`,
            }),
        );
    }

    /**
     * The confirmation to open from a work row, as a router link.
     *
     * The row shows its times but does not edit them, so while the confirmation is still open the
     * notification number it carries is the way to the place that does. Built here rather than in the
     * row because the route is the page's knowledge, like every other navigation on it.
     *
     * The type comes from the confirmation that recorded the row, read from the confirmation slice.
     * It used to be derived from the notification's available statuses, which answers a different
     * question — which type it *could* be confirmed with now — and so sent the technician to a short
     * confirmation when the times had been recorded against a normal one that was equally on offer.
     * Reading the store also keeps this off any service's initialization order, which is what broke
     * the page when a row could first be rendered during startup.
     *
     * @param row the work row in question
     * @returns the router link, or [] when the row names no notification to open
     */
    protected confirmationLink(row: TimesheetWorkRow): string[] {
        const { frontId, qmNum, maNum } = row.notificationId;
        // Only while the confirmation is still open: a submitted one has nothing left to correct, so
        // its row names its notification without offering a way in.
        if (!row.inProgress || !frontId || !qmNum) {
            return [];
        }

        // A row whose confirmation has been submitted no longer has one — the submit removes it — and a
        // normal confirmation is where such a row's link pointed before this too: a completed
        // notification offers no next status for a short one to be read from.
        const confirmationType = row.confirmationType ?? CONFIRMATION_TYPES.NORMAL;

        return ['/' + ConfigConstants.routeUrlConfirmation, frontId, qmNum, maNum, confirmationType];
    }

    /**
     * Whether a travel-back leg's kind may be switched on this row.
     *
     * Two conditions, both of which the page rather than the projection knows: standby travel has
     * to be enabled at all, and only the leg the projection marked editable — the timesheet's last
     * travel-back — may change, because the earlier legs are settled.
     *
     * @param row the work row in question
     * @returns whether to offer the travel-type dropdown
     */
    protected canEditTravelType(row: TimesheetWorkRow): boolean {
        if (!this.enableStandbyTravel() || !row.isTravelBackEditable || row.inProgress) {
            return false;
        }
        // A manual entry right after the leg continues it, which settles the leg's kind.
        const rows = this.rows();
        return rows[rows.indexOf(row) + 1]?.kind !== 'manual';
    }

    /**
     * @param row the work row in question
     * @returns whether to offer the means-of-transport dropdown rather than plain text
     */
    protected canEditMeansOfTransport(row: TimesheetWorkRow): boolean {
        return this.displayMeansOfTransport() && row.isTravelBackEditable && !row.inProgress;
    }

    /**
     * The means of transport as text, shown on the rows that do not offer the dropdown. Follows the
     * legacy rule: only the travel rows carry it, and only where the config displays it.
     *
     * @param row the work row in question
     * @returns the description of the row's means of transport, or ''
     */
    protected meansOfTransportText(row: TimesheetWorkRow): string {
        if (!this.displayMeansOfTransport() || this.canEditMeansOfTransport(row) || !row.meansOfTransport) {
            return '';
        }
        return this.meansOfTransportDescriptions()[row.meansOfTransport] ?? row.meansOfTransport;
    }

    /**
     * Whether to offer continuing a travel-back leg over this gap.
     *
     * Mirrors the legacy `showAddTravelBack`: with standby travel enabled it follows any travel-back
     * leg, and without it only the trailing gap qualifies, and only where the config allows an
     * additional travel back at all.
     *
     * @param row the gap in question
     * @returns whether to offer the travel-back button
     */
    protected canAddTravelBack(row: TimesheetGapRow): boolean {
        if (this.isReadonly() || !row.precedingTravelBack) {
            return false;
        }
        return this.enableStandbyTravel() || (this.displayAdditionalTravelBack() && row.isBeforeEndOfWork);
    }

    /**
     * Accounts for a gap as another leg of the travel back it follows, inheriting that leg's
     * notification and billability and taking the opposite billable/standby kind.
     *
     * @param row the gap being accounted for
     */
    protected onAddTravelBack(row: TimesheetGapRow): void {
        const timesheet = this.selectedTimesheet();
        const origin = row.precedingTravelBack;
        if (!timesheet || !origin) {
            return;
        }
        this.store.dispatch(
            timesheetManualEntryAdded({
                timesheetId: timesheet.timesheetId,
                entryId: getUuidV4(),
                start: row.start,
                end: row.end,
                notificationId: origin.notificationId,
                travelType: followUpTravelType(origin.workCode),
                isTravelBillable: origin.isTravelBillable,
                correlationId: `timesheet-add-travel-back-${++this.writeCounter}`,
            }),
        );
    }

    /**
     * @param row the manual entry to remove
     */
    protected onDeleteEntry(row: TimesheetManualRow): void {
        const timesheet = this.selectedTimesheet();
        if (!timesheet) {
            return;
        }
        this.store.dispatch(
            timesheetEntryDeleted({
                timesheetId: timesheet.timesheetId,
                entryId: row.entryId,
                correlationId: `timesheet-delete-entry-${++this.writeCounter}`,
            }),
        );
    }

    /**
     * Pulls the end of work back onto the gap's start, closing it.
     *
     * That instant is where the day is accounted for up to, which is the last entry's end when there
     * is one and the start of work when there is not — so a timesheet with nothing recorded on it yet
     * can be closed up too. Reading it from the gap row rather than searching for the last entry is
     * what makes that work: the button is offered whenever that row is a gap, and searching for an
     * entry found nothing to pull back to when the timesheet had none.
     *
     * Writes the model rather than dispatching directly, because the end of work is a form value:
     * this is the same edit as typing it, and the write-through carries it out for the same
     * reason it carries out a typed one.
     */
    protected onCollapseEndOfWorkGap(): void {
        const gap = this.trailingGap();
        if (!gap) {
            return;
        }
        this.model.update((model) => ({ ...model, endOfWork: gap.start }));
    }

    /**
     * Opens the address picker for one manual entry and writes the choice into the model.
     *
     * The address is a form value like any other, so it goes through the model: the
     * `addressRequired` rule sees it at once and the row stops reading as incomplete, while the
     * write-through puts it in the store. What travels is the chosen address's id — the picker
     * hands back a reference into the address slice, not a copy of the address itself.
     *
     * @param row the manual entry whose address is being chosen
     */
    protected onSelectAddress(row: TimesheetManualRow): void {
        const picker = this.modalService.show(AddressPickerComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'address-picker-modal-title',
        });

        picker.content?.addressSelected.subscribe((address: string) => {
            this.model.update((model) => ({
                ...model,
                entries: model.entries.map((entry) => (entry.entryId === row.entryId ? { ...entry, address } : entry)),
            }));
        });
    }

    /**
     * Sends the timesheet.
     *
     * There is nothing to save first: every edit is already in the slice the submit effect builds
     * its out model from. `submit` is still what starts this, for what it does besides saving —
     * it marks every field touched and refuses while the form is invalid. Then the two
     * acknowledge-once warnings, then the dispatch. The in-progress-confirmation gate is not
     * repeated here: it already disables the button.
     */
    protected async send(): Promise<void> {
        const timesheet = this.selectedTimesheet();
        if (
            !timesheet ||
            this.isSubmitting() ||
            !(await submit(this.timesheetForm, () => Promise.resolve(undefined)))
        ) {
            return;
        }

        if (!this.acknowledgeWarnings(timesheet)) {
            return;
        }

        this.isSubmitting.set(true);
        const loading = new LoadingScreenModel(this.message('TIMESHEET_SUBMIT_MESSAGE'));
        const loadingModal = this.modalService.show(LoadingScreenComponent, {
            class: 'modal-sm',
            initialState: { model: loading },
            ignoreBackdropClick: true,
            ariaLabelledBy: 'loading-screen-modal-title',
        });

        try {
            await this.dispatchSubmit();
            await this.router.navigate([ConfigConstants.routeUrlDashboard]);
        } catch (error) {
            this.reportSubmitFailure(error);
        } finally {
            this.isSubmitting.set(false);
            loadingModal.hide();
        }
    }

    /**
     * Shows whichever warning still needs acknowledging, and reports whether the submit may go on.
     *
     * Each warning is shown once and then remembered, so acknowledging it and pressing send again
     * proceeds. The end-of-work acknowledgement is forgotten when that value changes, matching the
     * legacy behaviour: a *different* future end of work is a new thing to be warned about.
     *
     * @param timesheet the timesheet being sent
     * @returns whether every warning has been acknowledged
     */
    private acknowledgeWarnings(timesheet: TimesheetState): boolean {
        if (this.acknowledgedFutureEndOfWork() !== timesheet.endOfWork && isEndOfWorkInFuture(timesheet, this.now())) {
            this.modalService
                .show(YesNoModalDialogComponent, {
                    class: ModalClass.MODAL_MEDIUM,
                    backdrop: 'static',
                    keyboard: false,
                    ariaLabelledBy: 'yes-no-modal-title',
                    initialState: {
                        model: new YesNoModalDialogModel(
                            this.message('TIMESHEET_EOW_IN_FUTURE_WARNING_TITLE'),
                            this.message('TIMESHEET_EOW_IN_FUTURE_WARNING_BODY'),
                            this.message('TIMESHEET_EOW_IN_FUTURE_WARNING_OK_ACTION'),
                            this.message('TIMESHEET_EOW_IN_FUTURE_WARNING_NO_ACTION'),
                        ),
                    },
                })
                .content?.modalResult.pipe(take(1))
                .subscribe((accepted: boolean) => {
                    if (accepted) {
                        this.acknowledgedFutureEndOfWork.set(timesheet.endOfWork);
                    }
                });
            return false;
        }

        if (
            !this.acknowledgedLongDay() &&
            exceedsMaxWorkingHours(timesheet, this.maxWorkingHours(), this.pauseCodes())
        ) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                this.message('TIMESHEET_TIME_HOURS_EXCEEDING_TITLE'),
                <string>this.translate.instant('TIMESHEET_TIME_HOURS_EXCEEDING_LABEL', {
                    workhour: this.maxWorkingHours(),
                }),
                () => this.acknowledgedLongDay.set(true),
            );
            return false;
        }

        return true;
    }

    /**
     * Dispatches the submit and settles once the offline queue flush that follows it completes.
     *
     * The outcome subscription is set up before dispatching so a synchronous result cannot be
     * missed, and the correlation id keeps this call from settling on a concurrent submit — the
     * same bridge the facade used, without the facade.
     *
     * @returns a promise resolving on success and rejecting with the raw error string
     */
    private dispatchSubmit(): Promise<void> {
        const correlationId = `timesheet-submit-${++this.writeCounter}`;
        const outcome = firstValueFrom(
            this.actions$.pipe(
                ofType(timesheetSubmitSynced, timesheetSubmitFailed),
                filter((action) => action.correlationId === correlationId),
                take(1),
            ),
        ).then((action) => {
            if (action.type === timesheetSubmitFailed.type) {
                // rejected with the raw string, because the mapping below matches error-code enums
                throw action.error;
            }
        });

        this.store.dispatch(submitTimesheetRequested({ correlationId }));
        return outcome;
    }

    /**
     * Turns a submit failure into the notification the technician sees.
     *
     * Matches the raw error strings against the error-code enums, which is why they travel
     * unwrapped through the store.
     *
     * @param error the raw error the submit reported
     */
    private reportSubmitFailure(error: unknown): void {
        const [titleKey, bodyKey] =
            error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                ? ['OFFLINE_STORE_ERROR_TITLE', 'OFFLINE_STORE_ERROR_BODY']
                : error === CONCURRENT_USAGE_ERROR_CODES.TIMESHEET_OUTDATED
                  ? ['TIMESHEET_REMOTE_UPDATE_REQUIRED_TITLE', 'TIMESHEET_REMOTE_UPDATE_REQUIRED_BODY']
                  : error === CONCURRENT_USAGE_ERROR_CODES.NOTIFICATIONS_OUTDATED
                    ? ['TIMESHEET_NOTIFICATION_IN_PROGRESS_ERROR_TITLE', 'TIMESHEET_IN_PROGRESS_BLOCKS_SUBMIT_MESSAGE']
                    : ['TIMESHEET_REQUEST_ERROR_TITLE', 'TIMESHEET_REQUEST_ERROR_BODY'];

        this.appNotificationService.setAppNotification(
            AppNotificationType.ERROR,
            this.message(titleKey),
            this.message(bodyKey),
        );
    }

    /**
     * {@link TimesheetCanDeactivateHost}: nothing to do.
     *
     * Every edit worth keeping reached the store when it was made and the persist effect wrote it
     * out then, so there is no pending state to flush on the way out. What the form may still be
     * holding is an edit {@link writeThrough} refused, and that is exactly what should not be
     * saved. The method stays because the guard is shared with the legacy component, which does
     * still save here.
     *
     * @returns an observable that completes at once
     */
    public saveBeforeDeactivate(): Observable<unknown> {
        return of(undefined);
    }

    /**
     * Builds the action carrying one edit of the form.
     *
     * @param model the edited model
     * @returns the action to dispatch
     */
    private editAction(model: TimesheetFormModel): ReturnType<typeof timesheetEdited> {
        return timesheetEdited({
            timesheetId: model.timesheetId,
            startOfWork: model.startOfWork,
            endOfWork: model.endOfWork,
            entries: toEntryPatches(model),
            correlationId: `timesheet-edit-${++this.writeCounter}`,
        });
    }

    /**
     * The translation key for one order conflict.
     *
     * Every conflict the two span rules report is one of seven situations, and each of them has
     * something specific to say. Which one it is follows from the kind, from whether the marked
     * field belongs to an entry or to one of the two markers (`index === undefined`), and — for an
     * overlap, which marks both ends — from which half was marked.
     *
     * @param conflict the conflict to describe
     * @returns the translation key for it
     */
    private static conflictKey(conflict: SpanConflict): string {
        const onMarker = conflict.index === undefined;
        switch (conflict.kind) {
            case 'invertedSpan':
                return 'TIMESHEET_ERROR_ENDS_BEFORE_START';
            case 'overlappingSpans':
                return conflict.half === 'end' ? 'TIMESHEET_ERROR_OVERLAPS_NEXT' : 'TIMESHEET_ERROR_OVERLAPS_PREVIOUS';
            case 'itemBeforeSpan':
                return onMarker ? 'TIMESHEET_ERROR_START_OF_WORK_TOO_LATE' : 'TIMESHEET_ERROR_BEFORE_START_OF_WORK';
            case 'itemAfterSpan':
                return onMarker ? 'TIMESHEET_ERROR_END_OF_WORK_TOO_EARLY' : 'TIMESHEET_ERROR_AFTER_END_OF_WORK';
            default:
                return 'TIMESHEET_INVALID_MESSAGE';
        }
    }

    /**
     * What one order conflict says on the row it marks.
     *
     * Names the instant the value may not cross, because that is the part the technician can act on:
     * the generic "the timesheet is invalid" left them to work out which of two values to change and
     * by how much, on a page where several rows can be marked at once.
     *
     * The instant is formatted with the configured display pattern, in local time — the same time the
     * row's own inputs show, so the message and the field it sits under cannot disagree.
     *
     * @param conflict the conflict to describe
     * @returns the message to show on the marked field
     */
    private conflictMessage(conflict: SpanConflict): string {
        return this.formatMessage(TimesheetNgrxComponent.conflictKey(conflict), conflict.limit);
    }

    /**
     * What one bounds-collection conflict says on the field it marks.
     *
     * The bound and the item that beat it disagree for the same reason but are not the same
     * mistake to fix, so each gets the key that speaks to it: the bound's own key when it is the
     * bound being marked, and the matching entry-side key — already worded for a slot rather than
     * a marker — when it is the entry.
     *
     * @param conflict the conflict to describe
     * @param boundKey the key for the bound's own conflict
     * @param itemKey the key for an item that beat the bound
     * @returns the message to show on the marked field
     */
    private boundConflictMessage(conflict: BoundConflict, boundKey: string, itemKey: string): string {
        return this.formatMessage(conflict.index === undefined ? boundKey : itemKey, conflict.limit);
    }

    /**
     * Resolves a translation with the instant the offending value may not cross formatted into it.
     *
     * Rendered in local time and in the browser's locale — the same time, and the same reading of
     * it, the row's own inputs show, so the message and the field it sits under cannot disagree.
     * A bare `moment().format('L LT')` would not do: `L` and `LT` resolve against moment's global
     * locale, which the app pins to `en`, so every technician would read a US date and a 12h clock.
     *
     * @param key the translation key
     * @param limit the instant to format into it
     * @returns the resolved message
     */
    private formatMessage(key: string, limit: string): string {
        return <string>this.translate.instant(key, { limit: DateUtilities.formatDateTime(moment(limit)) });
    }

    /**
     * Resolves a translation lazily, so the schema does not translate before the language files
     * are in.
     *
     * @param key the translation key
     * @returns the resolved message
     */
    private message(key: string): string {
        return <string>this.translate.instant(key);
    }
}
